// Mock data for Report Modules (M0-M6)
// Used for Evidence, AI Insight, and Review tabs
import stationSt001 from '../assets/station-st-001-2025-01-15.png';
import stationSt002 from '../assets/station-st-002-2025-01-14.png';
import stationSt003 from '../assets/station-st-003-2025-01-13.png';
import i18n, { DEFAULT_LANGUAGE } from '../i18n/config';
import { resolveLanguageCode } from './locale';

const resolveLocale = (locale) => locale || i18n.language || DEFAULT_LANGUAGE;
const getLocalizedVariantKey = (key, localeCode) => {
  if (localeCode === 'en') {
    return `${key}En`;
  }
  if (localeCode === 'es') {
    return `${key}Es`;
  }
  return null;
};

const localizeObject = (value, locale) => {
  const localeCode = resolveLanguageCode(locale);
  if (Array.isArray(value)) {
    return value.map(item => localizeObject(item, localeCode));
  }
  if (value && typeof value === 'object') {
    const result = {};
    Object.keys(value).forEach((key) => {
      if (key.endsWith('En') || key.endsWith('Es')) {
        return;
      }
      const localizedVariantKey = getLocalizedVariantKey(key, localeCode);
      const englishKey = `${key}En`;
      if (localizedVariantKey && Object.prototype.hasOwnProperty.call(value, localizedVariantKey)) {
        result[key] = localizeObject(value[localizedVariantKey], localeCode);
        return;
      }
      if (localeCode !== 'it' && Object.prototype.hasOwnProperty.call(value, englishKey)) {
        result[key] = localizeObject(value[englishKey], localeCode);
        return;
      }
      result[key] = localizeObject(value[key], localeCode);
    });
    return result;
  }
  return value;
};

// ============ MODULE 0: WELCOME / CURRENT FIELD SITUATION ============

export const M0_EVIDENCE_DATA = {
  agronomicalStatus: {
    ph: { value: 6.8, status: 'normal', unit: 'pH', optimal: { min: 6.0, max: 7.5 } },
    conductivity: { value: 1.2, status: 'normal', unit: 'dS/m', optimal: { min: 0.5, max: 2.0 } },
    soilHumidity: { value: 65, status: 'normal', unit: '%', optimal: { min: 30, max: 60 } },
  },
  cropInfo: {
    name: 'Wheat',
    variety: 'Durum Wheat - Simeto',
    growthStage: 'Tillering',
    plantingDate: '2024-10-15',
    expectedHarvest: '2025-06-20',
    area: 45.5, // hectares
  },
  landParcels: [
    {
      id: 'P001',
      sheet: '123',
      number: '45',
      area: 12.5,
      cropType: 'Wheat',
      status: 'active',
      lastActivity: '2025-01-10',
      coordinates: [45.123, 7.456],
    },
    {
      id: 'P002',
      sheet: '124',
      number: '46',
      area: 8.3,
      cropType: 'Wheat',
      status: 'active',
      lastActivity: '2025-01-08',
      coordinates: [45.125, 7.458],
    },
    {
      id: 'P003',
      sheet: '125',
      number: '47',
      area: 15.2,
      cropType: 'Barley',
      status: 'active',
      lastActivity: '2025-01-12',
      coordinates: [45.127, 7.460],
    },
  ],
  stationImages: [
    {
      id: 'img1',
      url: stationSt001,
      stationName: 'Station ST-001',
      date: '2025-01-15',
      thumbnail: stationSt001,
    },
    {
      id: 'img2',
      url: stationSt002,
      stationName: 'Station ST-002',
      date: '2025-01-14',
      thumbnail: stationSt002,
    },
    {
      id: 'img3',
      url: stationSt003,
      stationName: 'Station ST-003',
      date: '2025-01-13',
      thumbnail: stationSt003,
    },
  ],
  mode: 'plants', // 'plants' or 'animals'
};

export const M0_AI_INSIGHT = {
  summary: 'Based on current field data, the crop is in good condition with optimal soil parameters. pH levels are within acceptable range (6.8), conductivity is normal (1.2 dS/m), and soil humidity is adequate (65%). The wheat crop is in the tillering stage and progressing well. No immediate concerns detected.',
  keyFindings: [
    {
      id: 'f1',
      type: 'positive',
      title: 'Optimal Soil Conditions',
      description: 'All soil parameters are within optimal ranges for wheat cultivation.',
    },
    {
      id: 'f2',
      type: 'positive',
      title: 'Healthy Crop Development',
      description: 'Wheat is progressing normally through the tillering stage.',
    },
    {
      id: 'f3',
      type: 'warning',
      title: 'Monitor Humidity Levels',
      description: 'Soil humidity is adequate but should be monitored as temperatures rise.',
    },
  ],
  riskAssessment: {
    level: 'low', // low, medium, high
    description: 'Current field conditions present minimal risk. All monitored parameters are within acceptable ranges. Continue standard monitoring practices.',
  },
  recommendations: [
    'Continue monitoring soil humidity levels, especially during warmer periods.',
    'Consider applying nitrogen fertilizer during the next growth stage.',
    'Maintain current irrigation schedule based on soil moisture readings.',
    'Schedule soil analysis before the next planting season.',
  ],
  confidenceScore: 0.87,
};

export const M0_REVIEW_DATA = {
  summary: '',
  claims: [
    {
      id: 'claim1',
      text: 'Soil pH is within optimal range for wheat cultivation (6.8).',
      citations: ['Station Data - ST-001', 'Soil Analysis Report 2024'],
      validated: true,
    },
    {
      id: 'claim2',
      text: 'Crop is progressing normally through the tillering stage.',
      citations: ['Field Observation', 'Growth Stage Analysis'],
      validated: false,
    },
  ],
};

// ============ MODULE 1: PRODUCTION TRENDS ============

export const M1_EVIDENCE_DATA = {
  year: 2024,
  availableYears: [2022, 2023, 2024],
  productionData: [
    { month: 'Jan', production: 0.9, ricaBenchmark: 3.2 },
    { month: 'Mar', production: 1.1, ricaBenchmark: 3.4 },
    { month: 'May', production: 1.3, ricaBenchmark: 3.6 },
    { month: 'Jul', production: 0.6, ricaBenchmark: 3.8 },
    { month: 'Sep', production: 0.8, ricaBenchmark: 3.5 },
    { month: 'Nov', production: 0.7, ricaBenchmark: 3.3 },
  ],
  totalProduction: 35.2,
  averagePerHectare: 0.77,
  ricaBenchmark: 8.0,
  percentageOfBenchmark: 9.6,
};

export const M1_AI_INSIGHT = {
  summary: 'Production is below the RICA benchmark, with the largest gap during peak summer months. The trend suggests water stress and heat events influenced yields.',
  comparison: 'Production averages 9.6% of the benchmark, indicating a significant deviation that requires agronomic justification.',
  recommendations: [
    'Review irrigation scheduling during July-August.',
    'Evaluate heat stress mitigation strategies under panels.',
    'Reassess fertilization timing to improve grain filling.',
  ],
  anomalies: [
    'July production dipped below 0.3 tons/ha due to heat stress.',
    'August recovery lagged behind the benchmark by over 70%.',
  ],
};

export const M1_REVIEW_DATA = {
  summary: 'Production trends show a sustained gap vs RICA benchmarks. Further validation of July-August measurements is recommended.',
  claims: [
    {
      id: 'm1-claim-1',
      text: 'Production remained below 30% of the RICA benchmark for multiple summer periods.',
      citations: ['PLV Summary 2024', 'Station Data - ST-002'],
      validated: false,
    },
  ],
};

// ============ MODULE 2: RECUPERO FERTILITA DEL SUOLO ============

export const M2_EVIDENCE_DATA = {
  title: 'Recupero Fertilita del Suolo',
  titleEn: 'Soil Fertility Recovery',

  titleEs: 'Recuperación de la fertilidad del suelo',
  description: 'Monitoriamo i parametri chiave del suolo per valutare il recupero della fertilita.',
  descriptionEn: 'We monitor key soil parameters to assess fertility recovery.',

  descriptionEs: 'Monitoreamos parámetros clave del suelo para evaluar la recuperación de la fertilidad.',
  relevantSensors: ['Umidita del suolo', 'pH', 'Conducibilita'],
  relevantSensorsEn: ['Soil moisture', 'pH', 'Conductivity'],
  sensors: [
    {
      id: 'soil-moisture',
      name: 'Umidita del suolo',
      nameEn: 'Soil moisture',

      nameEs: 'Humedad del suelo',
      description: 'Volumetric Water Content nei primi strati di terreno.',
      descriptionEn: 'Volumetric water content in the upper soil layers.',

      descriptionEs: 'Contenido volumétrico de agua en las capas superiores del suelo.',
      charts: [
        {
          id: 'soil-moisture-stable',
          title: 'Stabilita nel periodo primaverile',
          titleEn: 'Stability in the spring period',

          titleEs: 'Estabilidad en el período de primavera.',
          note: 'Il tracciato mostra valori di umidita del suolo stabili durante la primavera, con oscillazioni contenute tra i mesi osservati. La stabilita suggerisce una buona capacita di ritenzione idrica e una gestione irrigua coerente. Il grafico serve da riferimento per confronti con periodi successivi o eventi estremi.',
          noteEn: 'The curve shows stable soil moisture values during spring, with limited fluctuations across the observed months. The stability suggests good water retention and consistent irrigation management. The chart serves as a reference for comparisons with later periods or extreme events.',

          noteEs: 'La curva muestra valores estables de humedad del suelo durante la primavera, con fluctuaciones limitadas a lo largo de los meses observados. La estabilidad sugiere una buena retención de agua y un manejo consistente del riego. El gráfico sirve como referencia para comparaciones con períodos posteriores o eventos extremos.',
          unit: '%',
          data: [
            { label: 'Mar', value: 22 },
            { label: 'Apr', value: 23 },
            { label: 'May', value: 22 },
            { label: 'Jun', value: 23 },
            { label: 'Jul', value: 22 },
          ],
        },
        {
          id: 'soil-moisture-spike',
          title: 'Risposta ad irrigazione',
          titleEn: 'Response to irrigation',

          titleEs: 'Respuesta al riego',
          note: 'Dopo l\'irrigazione si osserva un incremento rapido dell\'umidita, seguito da un rientro graduale. La dinamica indica un assorbimento efficace e una perdita controllata per evaporazione e drenaggio. Questa lettura aiuta a calibrare tempi e volumi degli interventi.',
          noteEn: 'After irrigation, a rapid increase in moisture is observed, followed by a gradual decline. The dynamics indicate effective uptake and controlled loss from evaporation and drainage. This reading helps calibrate timing and volumes of interventions.',

          noteEs: 'Después del riego, se observa un rápido aumento de la humedad, seguido de una disminución gradual. La dinámica indica una absorción efectiva y una pérdida controlada por evaporación y drenaje. Esta lectura ayuda a calibrar el momento y el volumen de las intervenciones.',
          unit: '%',
          data: [
            { label: 'Week 1', value: 18 },
            { label: 'Week 2', value: 19 },
            { label: 'Week 3', value: 28 },
            { label: 'Week 4', value: 24 },
            { label: 'Week 5', value: 21 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt001,
            alt: 'Campo aperto - Marzo',
            altEn: 'Open field - March',

            altEs: 'Campo abierto - marzo',
            caption: 'Immagine di campo aperto usata come evidenza visiva del monitoraggio dell\'umidita del suolo. La scena supporta le letture di stabilita primaverile e mostra le condizioni superficiali nel periodo osservato. Serve a confrontare l\'andamento con le aree retro modulo.',
            captionEn: 'Open field image used as visual evidence for soil moisture monitoring. The scene supports the spring stability readings and shows surface conditions during the observed period. It helps compare trends with the rear module areas.',

            captionEs: 'Imagen de campo abierto utilizada como evidencia visual para el monitoreo de la humedad del suelo. La escena respalda las lecturas de estabilidad del resorte y muestra las condiciones de la superficie durante el período observado. Ayuda a comparar tendencias con las áreas del módulo trasero.',
          },
          {
            url: stationSt002,
            alt: 'Campo aperto - Maggio',
            altEn: 'Open field - May',

            altEs: 'Campo abierto - mayo',
            caption: 'Immagine di campo aperto usata come evidenza visiva del monitoraggio dell\'umidita del suolo. La scena supporta le letture di stabilita primaverile e mostra le condizioni superficiali nel periodo osservato. Serve a confrontare l\'andamento con le aree retro modulo.',
            captionEn: 'Open field image used as visual evidence for soil moisture monitoring. The scene supports the spring stability readings and shows surface conditions during the observed period. It helps compare trends with the rear module areas.',

            captionEs: 'Imagen de campo abierto utilizada como evidencia visual para el monitoreo de la humedad del suelo. La escena respalda las lecturas de estabilidad del resorte y muestra las condiciones de la superficie durante el período observado. Ayuda a comparar tendencias con las áreas del módulo trasero.',
          },
        ],
        retroModulo: [
          {
            url: stationSt003,
            alt: 'Retro modulo - Marzo',
            altEn: 'Rear module - March',

            altEs: 'Módulo trasero - marzo',
            caption: 'Immagine del retro modulo durante il monitoraggio dell\'umidita del suolo. L\'ombreggiamento e la copertura vegetale aiutano a interpretare i valori piu stabili rispetto al campo aperto. La foto integra i dati dei sensori nel periodo analizzato.',
            captionEn: 'Rear module image during soil moisture monitoring. Shading and vegetation cover help explain more stable values compared to the open field. The photo complements sensor data for the analyzed period.',

            captionEs: 'Imagen del módulo trasero durante el monitoreo de la humedad del suelo. La sombra y la cobertura vegetal ayudan a explicar valores más estables en comparación con el campo abierto. La fotografía complementa los datos de los sensores del período analizado.',
          },
          {
            url: stationSt001,
            alt: 'Retro modulo - Maggio',
            altEn: 'Rear module - May',

            altEs: 'Módulo trasero - mayo',
            caption: 'Immagine del retro modulo durante il monitoraggio dell\'umidita del suolo. L\'ombreggiamento e la copertura vegetale aiutano a interpretare i valori piu stabili rispetto al campo aperto. La foto integra i dati dei sensori nel periodo analizzato.',
            captionEn: 'Rear module image during soil moisture monitoring. Shading and vegetation cover help explain more stable values compared to the open field. The photo complements sensor data for the analyzed period.',

            captionEs: 'Imagen del módulo trasero durante el monitoreo de la humedad del suelo. La sombra y la cobertura vegetal ayudan a explicar valores más estables en comparación con el campo abierto. La fotografía complementa los datos de los sensores del período analizado.',
          },
        ],
      },
      explanation: 'L\'umidita resta stabile nelle settimane piu fresche, con un picco dopo gli interventi irrigui. Il retro modulo mantiene valori leggermente piu alti.',
      explanationEn: 'Moisture remains stable in cooler weeks, with a peak after irrigation events. The rear module area maintains slightly higher values.',

      explanationEs: 'La humedad se mantiene estable en las semanas más frías, con un pico después de los eventos de riego. La zona del módulo trasero mantiene valores ligeramente superiores.',
      approved: false,
    },
    {
      id: 'soil-ph',
      name: 'pH del suolo',
      nameEn: 'Soil pH',

      nameEs: 'pH del suelo',
      description: 'Indice di acidita e fertilita del suolo.',
      descriptionEn: 'Indicator of soil acidity and fertility.',

      descriptionEs: 'Indicador de acidez y fertilidad del suelo.',
      charts: [
        {
          id: 'soil-ph-stable',
          title: 'pH stabile',
          titleEn: 'Stable pH',

          titleEs: 'pH estable',
          note: 'Il pH resta nella fascia ottimale per la coltura, con variazioni minime tra i mesi monitorati. L\'assenza di oscillazioni marcate indica stabilita chimica del suolo. Il grafico supporta la verifica di eventuali interventi correttivi.',
          noteEn: 'The pH remains in the optimal range for the crop, with minimal variation across the monitored months. The lack of marked oscillations indicates soil chemical stability. The chart supports verification of any corrective interventions.',

          noteEs: 'El pH se mantiene en el rango óptimo para el cultivo, con una variación mínima a lo largo de los meses monitoreados. La falta de oscilaciones marcadas indica estabilidad química del suelo. El cuadro respalda la verificación de cualquier intervención correctiva.',
          unit: 'pH',
          data: [
            { label: 'Mar', value: 6.7 },
            { label: 'Apr', value: 6.8 },
            { label: 'May', value: 6.8 },
            { label: 'Jun', value: 6.9 },
            { label: 'Jul', value: 6.8 },
          ],
        },
        {
          id: 'soil-ph-shift',
          title: 'Leggera variazione',
          titleEn: 'Slight variation',

          titleEs: 'Ligera variación',
          note: 'Si nota una lieve crescita del pH in corrispondenza degli interventi di ammendamento. La risposta e graduale e resta entro limiti compatibili con la fertilita. La lettura conferma l\'efficacia degli apporti senza generare sbilanciamenti.',
          noteEn: 'A slight increase in pH is observed in correspondence with amendment interventions. The response is gradual and remains within limits compatible with fertility. The reading confirms the effectiveness of inputs without causing imbalances.',

          noteEs: 'Se observa un ligero aumento del pH en correspondencia con las intervenciones de enmienda. La respuesta es gradual y se mantiene dentro de límites compatibles con la fertilidad. La lectura confirma la eficacia de los insumos sin provocar desequilibrios.',
          unit: 'pH',
          data: [
            { label: 'Week 1', value: 6.6 },
            { label: 'Week 2', value: 6.6 },
            { label: 'Week 3', value: 6.8 },
            { label: 'Week 4', value: 6.9 },
            { label: 'Week 5', value: 6.8 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt002,
            alt: 'Campo aperto - pH campionamento',
            altEn: 'Open field - pH sampling',

            altEs: 'Campo abierto - muestreo de pH',
            caption: 'Scatto in campo aperto che documenta l\'area di campionamento per il pH del suolo. Il contesto visivo aiuta a collegare le misure alla gestione agronomica e alle condizioni del terreno. La foto completa l\'evidenza delle letture periodiche.',
            captionEn: 'Open field shot documenting the sampling area for soil pH. The visual context helps link measurements to agronomic management and soil conditions. The photo completes evidence of periodic readings.',

            captionEs: 'Toma de campo abierto que documenta el área de muestreo para el pH del suelo. El contexto visual ayuda a vincular las mediciones con el manejo agronómico y las condiciones del suelo. La foto completa la evidencia de lecturas periódicas.',
          },
          {
            url: stationSt003,
            alt: 'Campo aperto - pH campionamento',
            altEn: 'Open field - pH sampling',

            altEs: 'Campo abierto - muestreo de pH',
            caption: 'Scatto in campo aperto che documenta l\'area di campionamento per il pH del suolo. Il contesto visivo aiuta a collegare le misure alla gestione agronomica e alle condizioni del terreno. La foto completa l\'evidenza delle letture periodiche.',
            captionEn: 'Open field shot documenting the sampling area for soil pH. The visual context helps link measurements to agronomic management and soil conditions. The photo completes evidence of periodic readings.',

            captionEs: 'Toma de campo abierto que documenta el área de muestreo para el pH del suelo. El contexto visual ayuda a vincular las mediciones con el manejo agronómico y las condiciones del suelo. La foto completa la evidencia de lecturas periódicas.',
          },
        ],
        retroModulo: [
          {
            url: stationSt001,
            alt: 'Retro modulo - pH campionamento',
            altEn: 'Rear module - pH sampling',

            altEs: 'Módulo trasero - muestreo de pH',
            caption: 'Scatto nel retro modulo in corrispondenza del campionamento pH. L\'area ombreggiata offre un confronto diretto con il campo aperto per la stabilita chimica del suolo. L\'immagine supporta la lettura degli effetti degli ammendanti.',
            captionEn: 'Rear module shot at pH sampling. The shaded area offers a direct comparison with the open field for soil chemical stability. The image supports interpretation of amendment effects.',

            captionEs: 'Toma del módulo trasero en el muestreo de pH. El área sombreada ofrece una comparación directa con el campo abierto en cuanto a la estabilidad química del suelo. La imagen apoya la interpretación de los efectos de la enmienda.',
          },
          {
            url: stationSt002,
            alt: 'Retro modulo - pH campionamento',
            altEn: 'Rear module - pH sampling',

            altEs: 'Módulo trasero - muestreo de pH',
            caption: 'Scatto nel retro modulo in corrispondenza del campionamento pH. L\'area ombreggiata offre un confronto diretto con il campo aperto per la stabilita chimica del suolo. L\'immagine supporta la lettura degli effetti degli ammendanti.',
            captionEn: 'Rear module shot at pH sampling. The shaded area offers a direct comparison with the open field for soil chemical stability. The image supports interpretation of amendment effects.',

            captionEs: 'Toma del módulo trasero en el muestreo de pH. El área sombreada ofrece una comparación directa con el campo abierto en cuanto a la estabilidad química del suelo. La imagen apoya la interpretación de los efectos de la enmienda.',
          },
        ],
      },
      explanation: 'Il pH rimane nella fascia ottimale con una lieve crescita dopo gli interventi di ammendamento.',
      explanationEn: 'pH remains in the optimal range with a slight increase after amendment interventions.',

      explanationEs: 'El pH permanece en el rango óptimo con un ligero aumento después de las intervenciones de enmienda.',
      approved: false,
    },
    {
      id: 'soil-conductivity',
      name: 'Conducibilita',
      nameEn: 'Conductivity',

      nameEs: 'Conductividad',
      description: 'Indicatore di salinita e disponibilita nutrienti.',
      descriptionEn: 'Indicator of salinity and nutrient availability.',

      descriptionEs: 'Indicador de salinidad y disponibilidad de nutrientes.',
      charts: [
        {
          id: 'soil-conductivity-stable',
          title: 'Conducibilita controllata',
          titleEn: 'Controlled conductivity',

          titleEs: 'Conductividad controlada',
          note: 'La conducibilita rimane su valori costanti lungo il periodo considerato, senza segnali di accumulo salino. Questa stabilita suggerisce un equilibrio tra apporti nutritivi e lisciviazione. Il grafico e utile per monitorare eventuali deviazioni future.',
          noteEn: 'Conductivity remains at constant values over the period considered, with no signs of salinity buildup. This stability suggests a balance between nutrient inputs and leaching. The chart is useful for monitoring any future deviations.',

          noteEs: 'La conductividad se mantiene en valores constantes durante el período considerado, sin signos de acumulación de salinidad. Esta estabilidad sugiere un equilibrio entre los aportes de nutrientes y la lixiviación. El gráfico es útil para monitorear cualquier desviación futura.',
          unit: 'dS/m',
          data: [
            { label: 'Mar', value: 1.1 },
            { label: 'Apr', value: 1.2 },
            { label: 'May', value: 1.2 },
            { label: 'Jun', value: 1.3 },
            { label: 'Jul', value: 1.2 },
          ],
        },
        {
          id: 'soil-conductivity-spike',
          title: 'Picco dopo concimazione',
          titleEn: 'Spike after fertilization',

          titleEs: 'Pico después de la fertilización',
          note: 'Dopo la concimazione emerge un incremento temporaneo della conducibilita, seguito da un rapido rientro. Il picco indica una disponibilita nutritiva immediata senza persistenza nel tempo. La curva aiuta a valutare la durata dell\'effetto della fertilizzazione.',
          noteEn: 'After fertilization, a temporary increase in conductivity emerges, followed by a rapid return. The spike indicates immediate nutrient availability without persistence over time. The curve helps evaluate the duration of the fertilization effect.',

          noteEs: 'Después de la fertilización, surge un aumento temporal de la conductividad, seguido de un rápido retorno. El pico indica disponibilidad inmediata de nutrientes sin persistencia en el tiempo. La curva ayuda a evaluar la duración del efecto de fertilización.',
          unit: 'dS/m',
          data: [
            { label: 'Week 1', value: 1.0 },
            { label: 'Week 2', value: 1.1 },
            { label: 'Week 3', value: 1.6 },
            { label: 'Week 4', value: 1.3 },
            { label: 'Week 5', value: 1.2 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt003,
            alt: 'Campo aperto - conducibilita',
            altEn: 'Open field - conductivity',

            altEs: 'Campo abierto - conductividad',
            caption: 'Immagine di campo aperto relativa al monitoraggio della conducibilita del suolo. Le condizioni superficiali osservate forniscono contesto alle variazioni rilevate dai sensori. Questa evidenza visiva aiuta a interpretare i picchi post concimazione.',
            captionEn: 'Open field image related to soil conductivity monitoring. The observed surface conditions provide context for variations detected by sensors. This visual evidence helps interpret post fertilization spikes.',

            captionEs: 'Imagen de campo abierto relacionada con el monitoreo de la conductividad del suelo. Las condiciones de la superficie observadas proporcionan un contexto para las variaciones detectadas por los sensores. Esta evidencia visual ayuda a interpretar los picos posteriores a la fertilización.',
          },
          {
            url: stationSt001,
            alt: 'Campo aperto - conducibilita',
            altEn: 'Open field - conductivity',

            altEs: 'Campo abierto - conductividad',
            caption: 'Immagine di campo aperto relativa al monitoraggio della conducibilita del suolo. Le condizioni superficiali osservate forniscono contesto alle variazioni rilevate dai sensori. Questa evidenza visiva aiuta a interpretare i picchi post concimazione.',
            captionEn: 'Open field image related to soil conductivity monitoring. The observed surface conditions provide context for variations detected by sensors. This visual evidence helps interpret post fertilization spikes.',

            captionEs: 'Imagen de campo abierto relacionada con el monitoreo de la conductividad del suelo. Las condiciones de la superficie observadas proporcionan un contexto para las variaciones detectadas por los sensores. Esta evidencia visual ayuda a interpretar los picos posteriores a la fertilización.',
          },
        ],
        retroModulo: [
          {
            url: stationSt002,
            alt: 'Retro modulo - conducibilita',
            altEn: 'Rear module - conductivity',

            altEs: 'Módulo trasero - conductividad',
            caption: 'Immagine in retro modulo che accompagna le misure di conducibilita. L\'ombreggiamento e la copertura del suolo contribuiscono a spiegare il rapido rientro dei valori dopo la concimazione. La foto completa il confronto tra aree.',
            captionEn: 'Rear module image accompanying conductivity measurements. Shading and soil cover help explain the rapid return of values after fertilization. The photo completes the comparison between areas.',

            captionEs: 'Imagen del módulo trasero que acompaña a las mediciones de conductividad. La sombra y la cobertura del suelo ayudan a explicar el rápido retorno de los valores después de la fertilización. La foto completa la comparación entre zonas.',
          },
          {
            url: stationSt003,
            alt: 'Retro modulo - conducibilita',
            altEn: 'Rear module - conductivity',

            altEs: 'Módulo trasero - conductividad',
            caption: 'Immagine in retro modulo che accompagna le misure di conducibilita. L\'ombreggiamento e la copertura del suolo contribuiscono a spiegare il rapido rientro dei valori dopo la concimazione. La foto completa il confronto tra aree.',
            captionEn: 'Rear module image accompanying conductivity measurements. Shading and soil cover help explain the rapid return of values after fertilization. The photo completes the comparison between areas.',

            captionEs: 'Imagen del módulo trasero que acompaña a las mediciones de conductividad. La sombra y la cobertura del suelo ayudan a explicar el rápido retorno de los valores después de la fertilización. La foto completa la comparación entre zonas.',
          },
        ],
      },
      explanation: 'La conducibilita mostra un picco post concimazione, poi rientra rapidamente nei valori ottimali.',
      explanationEn: 'Conductivity shows a post fertilization spike, then quickly returns to optimal values.',

      explanationEs: 'La conductividad muestra un pico posterior a la fertilización y luego vuelve rápidamente a valores óptimos.',
      approved: false,
    },
  ],
};

export const M2_AI_INSIGHT = {
  summary: 'I parametri del suolo indicano un recupero stabile. I picchi osservati sono coerenti con gli interventi di irrigazione e concimazione.',
  summaryEn: 'Soil parameters indicate a stable recovery. Observed peaks align with irrigation and fertilization interventions.',

  summaryEs: 'Los parámetros del suelo indican una recuperación estable. Los picos observados se alinean con las intervenciones de riego y fertilización.',
  highlights: [
    'Umidita piu elevata sotto i moduli nelle settimane calde',
    'pH stabile nella fascia ottimale',
    'Conducibilita senza stress salino',
  ],
  highlightsEn: [
    'Higher moisture under the modules during hot weeks',
    'pH stable in the optimal range',
    'Conductivity without saline stress',
  ],
};

export const M2_REVIEW_DATA = {
  summary: '',
};

// ============ MODULE 3: RISPARMIO IDRICO ============

export const M3_EVIDENCE_DATA = {
  title: 'Risparmio Idrico',
  titleEn: 'Water Savings',

  titleEs: 'Ahorro de agua',
  description: 'Confrontiamo consumi e perdite idriche tra campo aperto e retro modulo.',
  descriptionEn: 'We compare water use and losses between open field and rear module areas.',

  descriptionEs: 'Comparamos el uso y las pérdidas de agua entre el campo abierto y las áreas del módulo trasero.',
  relevantSensors: ['Evapotraspirazione', 'Portata irrigua', 'Pioggia'],
  relevantSensorsEn: ['Evapotranspiration', 'Irrigation flow', 'Rainfall'],
  sensors: [
    {
      id: 'evapotranspiration',
      name: 'Evapotraspirazione',
      nameEn: 'Evapotranspiration',

      nameEs: 'Evapotranspiración',
      description: 'Differenza tra evaporazione e traspirazione nelle due aree.',
      descriptionEn: 'Difference between evaporation and transpiration in the two areas.',

      descriptionEs: 'Diferencia entre evaporación y transpiración en las dos zonas.',
      charts: [
        {
          id: 'et-gap',
          title: 'Riduzione ET sotto moduli',
          titleEn: 'ET reduction under modules',

          titleEs: 'Reducción de ET bajo módulos',
          note: 'La differenza di evapotraspirazione tra retro modulo e campo aperto aumenta con l\'avanzare della stagione calda. I valori suggeriscono un risparmio idrico piu evidente nei mesi estivi. Il grafico fornisce un quadro stagionale utile per la pianificazione irrigua.',
          noteEn: 'The evapotranspiration difference between rear module and open field increases as the hot season progresses. Values suggest more evident water savings in summer months. The chart provides a seasonal overview useful for irrigation planning.',

          noteEs: 'La diferencia de evapotranspiración entre el módulo trasero y el campo abierto aumenta a medida que avanza la temporada de calor. Los valores sugieren un ahorro de agua más evidente en los meses de verano. El gráfico proporciona una descripción estacional útil para la planificación del riego.',
          unit: 'mm',
          data: [
            { label: 'Apr', value: 3.4 },
            { label: 'May', value: 3.1 },
            { label: 'Jun', value: 2.6 },
            { label: 'Jul', value: 2.2 },
            { label: 'Aug', value: 2.1 },
          ],
        },
        {
          id: 'et-spike',
          title: 'Picco caldo estivo',
          titleEn: 'Summer heat spike',

          titleEs: 'Pico de calor en verano',
          note: 'Durante l\'evento caldo si registra la massima differenza di evapotraspirazione tra le due aree. Il picco evidenzia l\'effetto mitigante dell\'ombreggiamento dei moduli. Questa evidenza aiuta a spiegare le riduzioni di consumo nei periodi critici.',
          noteEn: 'During the hot event, the maximum evapotranspiration difference between the two areas is recorded. The peak highlights the mitigating effect of module shading. This evidence helps explain consumption reductions during critical periods.',

          noteEs: 'Durante el evento caluroso, se registra la diferencia máxima de evapotranspiración entre las dos áreas. El pico resalta el efecto mitigante del sombreado del módulo. Esta evidencia ayuda a explicar las reducciones del consumo durante períodos críticos.',
          unit: 'mm',
          data: [
            { label: 'Week 1', value: 4.1 },
            { label: 'Week 2', value: 4.4 },
            { label: 'Week 3', value: 5.2 },
            { label: 'Week 4', value: 4.7 },
            { label: 'Week 5', value: 4.0 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt001,
            alt: 'Campo aperto - Evapotraspirazione',
            altEn: 'Open field - Evapotranspiration',

            altEs: 'Campo abierto - Evapotranspiración',
            caption: 'Foto di campo aperto usata per documentare le condizioni durante il monitoraggio dell\'evapotraspirazione. La scena evidenzia l\'esposizione piena e aiuta a spiegare le differenze rispetto al retro modulo. L\'immagine rafforza la lettura dei trend stagionali.',
            captionEn: 'Open field photo used to document conditions during evapotranspiration monitoring. The scene highlights full exposure and helps explain differences versus the rear module. The image reinforces interpretation of seasonal trends.',

            captionEs: 'Fotografía de campo abierto utilizada para documentar las condiciones durante el monitoreo de la evapotranspiración. La escena resalta la exposición completa y ayuda a explicar las diferencias con el módulo trasero. La imagen refuerza la interpretación de las tendencias estacionales.',
          },
          {
            url: stationSt003,
            alt: 'Campo aperto - Evapotraspirazione',
            altEn: 'Open field - Evapotranspiration',

            altEs: 'Campo abierto - Evapotranspiración',
            caption: 'Foto di campo aperto usata per documentare le condizioni durante il monitoraggio dell\'evapotraspirazione. La scena evidenzia l\'esposizione piena e aiuta a spiegare le differenze rispetto al retro modulo. L\'immagine rafforza la lettura dei trend stagionali.',
            captionEn: 'Open field photo used to document conditions during evapotranspiration monitoring. The scene highlights full exposure and helps explain differences versus the rear module. The image reinforces interpretation of seasonal trends.',

            captionEs: 'Fotografía de campo abierto utilizada para documentar las condiciones durante el monitoreo de la evapotranspiración. La escena resalta la exposición completa y ayuda a explicar las diferencias con el módulo trasero. La imagen refuerza la interpretación de las tendencias estacionales.',
          },
        ],
        retroModulo: [
          {
            url: stationSt002,
            alt: 'Retro modulo - Evapotraspirazione',
            altEn: 'Rear module - Evapotranspiration',

            altEs: 'Módulo trasero - Evapotranspiración',
            caption: 'Foto nel retro modulo durante il monitoraggio dell\'evapotraspirazione. L\'ombreggiamento osservato supporta la riduzione delle perdite idriche nelle settimane calde. Questa evidenza visiva accompagna i picchi rilevati nei grafici.',
            captionEn: 'Rear module photo during evapotranspiration monitoring. Observed shading supports reduced water losses in hot weeks. This visual evidence accompanies peaks shown in the charts.',

            captionEs: 'Foto del módulo trasero durante el monitoreo de la evapotranspiración. El sombreado observado contribuye a reducir las pérdidas de agua en las semanas calurosas. Esta evidencia visual acompaña a los picos que se muestran en los gráficos.',
          },
          {
            url: stationSt001,
            alt: 'Retro modulo - Evapotraspirazione',
            altEn: 'Rear module - Evapotranspiration',

            altEs: 'Módulo trasero - Evapotranspiración',
            caption: 'Foto nel retro modulo durante il monitoraggio dell\'evapotraspirazione. L\'ombreggiamento osservato supporta la riduzione delle perdite idriche nelle settimane calde. Questa evidenza visiva accompagna i picchi rilevati nei grafici.',
            captionEn: 'Rear module photo during evapotranspiration monitoring. Observed shading supports reduced water losses in hot weeks. This visual evidence accompanies peaks shown in the charts.',

            captionEs: 'Foto del módulo trasero durante el monitoreo de la evapotranspiración. El sombreado observado contribuye a reducir las pérdidas de agua en las semanas calurosas. Esta evidencia visual acompaña a los picos que se muestran en los gráficos.',
          },
        ],
      },
      explanation: 'Le differenze tra campo aperto e retro modulo crescono nei mesi piu caldi, indicando un risparmio idrico costante.',
      explanationEn: 'Differences between open field and rear module areas grow in the hotter months, indicating consistent water savings.',

      explanationEs: 'Las diferencias entre las áreas de campo abierto y de los módulos traseros aumentan en los meses más cálidos, lo que indica un ahorro constante de agua.',
      approved: false,
    },
    {
      id: 'irrigation-flow',
      name: 'Portata irrigua',
      nameEn: 'Irrigation flow',

      nameEs: 'Flujo de riego',
      description: 'Volume di acqua irrigua utilizzato per appezzamento.',
      descriptionEn: 'Irrigation water volume used per plot.',

      descriptionEs: 'Volumen de agua de riego utilizado por parcela.',
      charts: [
        {
          id: 'flow-stable',
          title: 'Consumi contenuti',
          titleEn: 'Reduced consumption',

          titleEs: 'Consumo reducido',
          note: 'La portata irrigua mostra una riduzione graduale nel corso dei mesi, coerente con un fabbisogno in calo. La tendenza indica una gestione piu efficiente dell\'acqua rispetto all\'inizio stagione. Il grafico supporta la verifica degli obiettivi di risparmio.',
          noteEn: 'Irrigation flow shows a gradual reduction over the months, consistent with declining demand. The trend indicates more efficient water management compared to early season. The chart supports verification of savings targets.',

          noteEs: 'El caudal de riego muestra una reducción gradual a lo largo de los meses, consistente con la caída de la demanda. La tendencia indica una gestión del agua más eficiente en comparación con el inicio de la temporada. El gráfico respalda la verificación de los objetivos de ahorro.',
          unit: 'm3',
          data: [
            { label: 'Apr', value: 52 },
            { label: 'May', value: 50 },
            { label: 'Jun', value: 46 },
            { label: 'Jul', value: 44 },
            { label: 'Aug', value: 43 },
          ],
        },
        {
          id: 'flow-peak',
          title: 'Picco irrigazione',
          titleEn: 'Irrigation peak',

          titleEs: 'Pico de riego',
          note: 'Un picco di portata segnala un evento irriguo eccezionale legato a condizioni di stress. L\'aumento e circoscritto e non altera la traiettoria complessiva di riduzione. La lettura serve a contestualizzare l\'intervento straordinario.',
          noteEn: 'A flow peak signals an exceptional irrigation event tied to stress conditions. The increase is limited and does not alter the overall reduction trajectory. The reading helps contextualize the extraordinary intervention.',

          noteEs: 'Un pico de flujo indica un evento de riego excepcional vinculado a condiciones de estrés. El aumento es limitado y no altera la trayectoria general de reducción. La lectura ayuda a contextualizar la extraordinaria intervención.',
          unit: 'm3',
          data: [
            { label: 'Week 1', value: 40 },
            { label: 'Week 2', value: 42 },
            { label: 'Week 3', value: 58 },
            { label: 'Week 4', value: 48 },
            { label: 'Week 5', value: 45 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt002,
            alt: 'Campo aperto - Irrigazione',
            altEn: 'Open field - Irrigation',

            altEs: 'Campo abierto - Riego',
            caption: 'Immagine di campo aperto che accompagna le misure di portata irrigua per appezzamento. Il contesto visivo aiuta a collegare i consumi alle condizioni operative del periodo. Serve come supporto alle variazioni osservate nel grafico.',
            captionEn: 'Open field image accompanying irrigation flow measurements per plot. The visual context helps link consumption to operating conditions of the period. It supports the variations observed in the chart.',

            captionEs: 'Imagen de campo abierto que acompaña las mediciones de caudal de riego por parcela. El contexto visual ayuda a vincular el consumo con las condiciones operativas del período. Soporta las variaciones observadas en el gráfico.',
          },
          {
            url: stationSt003,
            alt: 'Campo aperto - Irrigazione',
            altEn: 'Open field - Irrigation',

            altEs: 'Campo abierto - Riego',
            caption: 'Immagine di campo aperto che accompagna le misure di portata irrigua per appezzamento. Il contesto visivo aiuta a collegare i consumi alle condizioni operative del periodo. Serve come supporto alle variazioni osservate nel grafico.',
            captionEn: 'Open field image accompanying irrigation flow measurements per plot. The visual context helps link consumption to operating conditions of the period. It supports the variations observed in the chart.',

            captionEs: 'Imagen de campo abierto que acompaña las mediciones de caudal de riego por parcela. El contexto visual ayuda a vincular el consumo con las condiciones operativas del período. Soporta las variaciones observadas en el gráfico.',
          },
        ],
        retroModulo: [
          {
            url: stationSt001,
            alt: 'Retro modulo - Irrigazione',
            altEn: 'Rear module - Irrigation',

            altEs: 'Módulo trasero - Riego',
            caption: 'Immagine del retro modulo durante le misure di portata irrigua. La presenza dei moduli suggerisce condizioni di ombreggiamento che possono ridurre il fabbisogno idrico. La foto integra la lettura dei picchi irrigui.',
            captionEn: 'Rear module image during irrigation flow measurements. The presence of modules suggests shading conditions that can reduce water demand. The photo complements interpretation of irrigation peaks.',

            captionEs: 'Imagen trasera del módulo durante las mediciones de caudal de riego. La presencia de módulos sugiere condiciones de sombra que pueden reducir la demanda de agua. La foto complementa la interpretación de los picos de riego.',
          },
          {
            url: stationSt002,
            alt: 'Retro modulo - Irrigazione',
            altEn: 'Rear module - Irrigation',

            altEs: 'Módulo trasero - Riego',
            caption: 'Immagine del retro modulo durante le misure di portata irrigua. La presenza dei moduli suggerisce condizioni di ombreggiamento che possono ridurre il fabbisogno idrico. La foto integra la lettura dei picchi irrigui.',
            captionEn: 'Rear module image during irrigation flow measurements. The presence of modules suggests shading conditions that can reduce water demand. The photo complements interpretation of irrigation peaks.',

            captionEs: 'Imagen trasera del módulo durante las mediciones de caudal de riego. La presencia de módulos sugiere condiciones de sombra que pueden reducir la demanda de agua. La foto complementa la interpretación de los picos de riego.',
          },
        ],
      },
      explanation: 'La portata irrigua mostra riduzioni graduali con picchi occasionali in corrispondenza dei periodi piu aridi.',
      explanationEn: 'Irrigation flow shows gradual reductions with occasional peaks during drier periods.',

      explanationEs: 'El flujo de riego muestra reducciones graduales con picos ocasionales durante los períodos más secos.',
      approved: false,
    },
    {
      id: 'rainfall',
      name: 'Pioggia utile',
      nameEn: 'Effective rainfall',

      nameEs: 'Precipitación efectiva',
      description: 'Cumulata di pioggia efficace per il terreno.',
      descriptionEn: 'Cumulative effective rainfall for the soil.',

      descriptionEs: 'Precipitación efectiva acumulada para el suelo.',
      charts: [
        {
          id: 'rainfall-season',
          title: 'Cumulata stagionale',
          titleEn: 'Seasonal cumulative',

          titleEs: 'Acumulado estacional',
          note: 'La cumulata di pioggia utile risulta distribuita in modo regolare durante la stagione monitorata. Questo contributo riduce la dipendenza dall\'irrigazione nei periodi di transizione. Il grafico aiuta a stimare il bilancio idrico complessivo.',
          noteEn: 'Cumulative effective rainfall is distributed evenly during the monitored season. This contribution reduces dependence on irrigation during transition periods. The chart helps estimate the overall water balance.',

          noteEs: 'La lluvia efectiva acumulada se distribuye uniformemente durante la temporada monitoreada. Esta contribución reduce la dependencia del riego durante los períodos de transición. El gráfico ayuda a estimar el balance hídrico general.',
          unit: 'mm',
          data: [
            { label: 'Apr', value: 32 },
            { label: 'May', value: 28 },
            { label: 'Jun', value: 18 },
            { label: 'Jul', value: 14 },
            { label: 'Aug', value: 20 },
          ],
        },
        {
          id: 'rainfall-event',
          title: 'Evento intenso',
          titleEn: 'Intense event',

          titleEs: 'evento intenso',
          note: 'L\'evento di pioggia piu intenso genera un picco localizzato nella cumulata settimanale. Pur concentrato, l\'apporto risulta significativo per il ripristino dell\'umidita del suolo. La curva evidenzia l\'importanza di questi episodi per la gestione irrigua.',
          noteEn: 'The most intense rainfall event creates a localized spike in weekly cumulative totals. Although concentrated, the contribution is significant for restoring soil moisture. The curve highlights the importance of these episodes for irrigation management.',

          noteEs: 'El evento de lluvia más intenso crea un pico localizado en los totales acumulados semanales. Aunque concentrado, el aporte es significativo para restaurar la humedad del suelo. La curva resalta la importancia de estos episodios para la gestión del riego.',
          unit: 'mm',
          data: [
            { label: 'Week 1', value: 4 },
            { label: 'Week 2', value: 6 },
            { label: 'Week 3', value: 22 },
            { label: 'Week 4', value: 8 },
            { label: 'Week 5', value: 5 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt003,
            alt: 'Campo aperto - Pioggia',
            altEn: 'Open field - Rainfall',

            altEs: 'Campo abierto - Precipitaciones',
            caption: 'Scatto in campo aperto che documenta il contesto delle misure di pioggia utile. L\'immagine aiuta a interpretare la distribuzione delle precipitazioni nel periodo monitorato. Supporta il confronto con il contributo irriguo.',
            captionEn: 'Open field shot documenting the context of effective rainfall measurements. The image helps interpret precipitation distribution during the monitored period. It supports comparison with the irrigation contribution.',

            captionEs: 'Toma de campo abierto que documenta el contexto de mediciones efectivas de lluvia. La imagen ayuda a interpretar la distribución de la precipitación durante el período monitoreado. Soporta comparación con el aporte de riego.',
          },
          {
            url: stationSt001,
            alt: 'Campo aperto - Pioggia',
            altEn: 'Open field - Rainfall',

            altEs: 'Campo abierto - Precipitaciones',
            caption: 'Scatto in campo aperto che documenta il contesto delle misure di pioggia utile. L\'immagine aiuta a interpretare la distribuzione delle precipitazioni nel periodo monitorato. Supporta il confronto con il contributo irriguo.',
            captionEn: 'Open field shot documenting the context of effective rainfall measurements. The image helps interpret precipitation distribution during the monitored period. It supports comparison with the irrigation contribution.',

            captionEs: 'Toma de campo abierto que documenta el contexto de mediciones efectivas de lluvia. La imagen ayuda a interpretar la distribución de la precipitación durante el período monitoreado. Soporta comparación con el aporte de riego.',
          },
        ],
        retroModulo: [
          {
            url: stationSt002,
            alt: 'Retro modulo - Pioggia',
            altEn: 'Rear module - Rainfall',

            altEs: 'Módulo trasero - Lluvia',
            caption: 'Scatto nel retro modulo legato alle misure di pioggia utile. La foto mostra l\'area ombreggiata dove l\'acqua disponibile puo avere dinamiche diverse. Questa evidenza accompagna i picchi localizzati nel grafico.',
            captionEn: 'Rear module shot linked to effective rainfall measurements. The photo shows the shaded area where available water may behave differently. This evidence accompanies localized peaks in the chart.',

            captionEs: 'Toma del módulo trasero vinculada a mediciones efectivas de lluvia. La foto muestra el área sombreada donde el agua disponible puede comportarse de manera diferente. Esta evidencia acompaña a los picos localizados en el gráfico.',
          },
          {
            url: stationSt003,
            alt: 'Retro modulo - Pioggia',
            altEn: 'Rear module - Rainfall',

            altEs: 'Módulo trasero - Lluvia',
            caption: 'Scatto nel retro modulo legato alle misure di pioggia utile. La foto mostra l\'area ombreggiata dove l\'acqua disponibile puo avere dinamiche diverse. Questa evidenza accompagna i picchi localizzati nel grafico.',
            captionEn: 'Rear module shot linked to effective rainfall measurements. The photo shows the shaded area where available water may behave differently. This evidence accompanies localized peaks in the chart.',

            captionEs: 'Toma del módulo trasero vinculada a mediciones efectivas de lluvia. La foto muestra el área sombreada donde el agua disponible puede comportarse de manera diferente. Esta evidencia acompaña a los picos localizados en el gráfico.',
          },
        ],
      },
      explanation: 'La pioggia utile integra il fabbisogno irriguo, con benefici maggiori nei periodi di bassa evaporazione.',
      explanationEn: 'Effective rainfall supplements irrigation needs, with greater benefits during low evaporation periods.',

      explanationEs: 'Una lluvia eficaz complementa las necesidades de riego, con mayores beneficios durante los períodos de baja evaporación.',
      approved: false,
    },
  ],
};

export const M3_AI_INSIGHT = {
  summary: 'I dati indicano un risparmio idrico consistente sotto i moduli, con differenze piu marcate nei mesi estivi.',
  summaryEn: 'Data indicates consistent water savings under the modules, with more pronounced differences in summer months.',

  summaryEs: 'Los datos indican un ahorro constante de agua bajo los módulos, con diferencias más pronunciadas en los meses de verano.',
  highlights: [
    'Riduzione ET tra 20% e 30% nei mesi caldi',
    'Portata irrigua stabilizzata dopo giugno',
    'Piogge efficaci nelle settimane di transizione',
  ],
  highlightsEn: [
    'ET reduction between 20% and 30% in hot months',
    'Irrigation flow stabilized after June',
    'Effective rains during transition weeks',
  ],
};

export const M3_REVIEW_DATA = {
  summary: '',
};

// ============ MODULE 4: MICROCLIMA ============

export const M4_EVIDENCE_DATA = {
  title: 'Microclima',
  titleEn: 'Microclimate',

  titleEs: 'Microclima',
  description: 'Analizziamo differenze di temperatura, umidita e radiazione tra campo aperto e retro modulo.',
  descriptionEn: 'We analyze differences in temperature, humidity, and radiation between open field and rear module areas.',

  descriptionEs: 'Analizamos diferencias de temperatura, humedad y radiación entre campo abierto y áreas de módulos traseros.',
  relevantSensors: ['Temperatura', 'Umidita', 'Irradiazione'],
  relevantSensorsEn: ['Temperature', 'Humidity', 'Irradiation'],
  sensors: [
    {
      id: 'temperature',
      name: 'Temperatura',
      nameEn: 'Temperature',

      nameEs: 'Temperatura',
      description: 'Delta termico tra campo aperto e retro modulo.',
      descriptionEn: 'Thermal delta between open field and rear module areas.',

      descriptionEs: 'Delta térmico entre campo abierto y áreas del módulo trasero.',
      charts: [
        {
          id: 'temp-gap',
          title: 'Riduzione temperatura',
          titleEn: 'Temperature reduction',

          titleEs: 'Reducción de temperatura',
          note: 'Le temperature sotto i moduli risultano piu basse rispetto al campo aperto, soprattutto nei mesi estivi. La riduzione dei picchi suggerisce un effetto di mitigazione termica costante. Il grafico supporta la valutazione dello stress termico sulla coltura.',
          noteEn: 'Temperatures under the modules are lower than in the open field, especially in summer months. The reduction of peaks suggests a constant thermal mitigation effect. The chart supports evaluation of heat stress on the crop.',

          noteEs: 'Las temperaturas debajo de los módulos son más bajas que en campo abierto, especialmente en los meses de verano. La reducción de los picos sugiere un efecto de mitigación térmica constante. El gráfico respalda la evaluación del estrés por calor en el cultivo.',
          unit: '°C',
          data: [
            { label: 'May', value: 2.1 },
            { label: 'Jun', value: 2.8 },
            { label: 'Jul', value: 3.4 },
            { label: 'Aug', value: 3.0 },
            { label: 'Sep', value: 2.2 },
          ],
        },
        {
          id: 'temp-event',
          title: 'Evento caldo',
          titleEn: 'Heat event',

          titleEs: 'Evento de calor',
          note: 'Durante l\'evento caldo si osserva la massima differenza di temperatura tra le due aree. Il retro modulo mantiene un microclima piu stabile anche nelle ore di massima insolazione. Questa evidenza rafforza il beneficio dell\'ombreggiamento nei giorni critici.',
          noteEn: 'During the heat event, the maximum temperature difference between the two areas is observed. The rear module maintains a more stable microclimate even during peak irradiation hours. This evidence reinforces the benefit of shading on critical days.',

          noteEs: 'Durante el evento de calor se observa la diferencia máxima de temperatura entre las dos áreas. El módulo trasero mantiene un microclima más estable incluso durante las horas pico de irradiación. Esta evidencia refuerza el beneficio de dar sombra en los días críticos.',
          unit: '°C',
          data: [
            { label: 'Week 1', value: 1.4 },
            { label: 'Week 2', value: 2.2 },
            { label: 'Week 3', value: 4.6 },
            { label: 'Week 4', value: 3.1 },
            { label: 'Week 5', value: 2.0 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt001,
            alt: 'Campo aperto - Temperatura',
            altEn: 'Open field - Temperature',

            altEs: 'Campo abierto - Temperatura',
            caption: 'Immagine di campo aperto legata al monitoraggio della temperatura. Il pieno irraggiamento visibile aiuta a contestualizzare i valori piu alti rispetto al retro modulo. La foto contribuisce alla lettura dei picchi stagionali.',
            captionEn: 'Open field image linked to temperature monitoring. Full irradiation helps contextualize higher values compared to the rear module. The photo contributes to reading seasonal peaks.',

            captionEs: 'Imagen de campo abierto vinculada al monitoreo de temperatura. La irradiación total ayuda a contextualizar valores más altos en comparación con el módulo trasero. La foto contribuye a la lectura de los picos estacionales.',
          },
          {
            url: stationSt002,
            alt: 'Campo aperto - Temperatura',
            altEn: 'Open field - Temperature',

            altEs: 'Campo abierto - Temperatura',
            caption: 'Immagine di campo aperto legata al monitoraggio della temperatura. Il pieno irraggiamento visibile aiuta a contestualizzare i valori piu alti rispetto al retro modulo. La foto contribuisce alla lettura dei picchi stagionali.',
            captionEn: 'Open field image linked to temperature monitoring. Full irradiation helps contextualize higher values compared to the rear module. The photo contributes to reading seasonal peaks.',

            captionEs: 'Imagen de campo abierto vinculada al monitoreo de temperatura. La irradiación total ayuda a contextualizar valores más altos en comparación con el módulo trasero. La foto contribuye a la lectura de los picos estacionales.',
          },
        ],
        retroModulo: [
          {
            url: stationSt003,
            alt: 'Retro modulo - Temperatura',
            altEn: 'Rear module - Temperature',

            altEs: 'Módulo trasero - Temperatura',
            caption: 'Immagine nel retro modulo che documenta le condizioni durante le misure di temperatura. L\'ombreggiamento dei pannelli appare coerente con la riduzione dei picchi termici. L\'evidenza visiva supporta il confronto microclimatico.',
            captionEn: 'Rear module image documenting conditions during temperature measurements. Panel shading aligns with reduced thermal peaks. The visual evidence supports the microclimate comparison.',

            captionEs: 'Imagen del módulo trasero que documenta las condiciones durante las mediciones de temperatura. El sombreado del panel se alinea con picos térmicos reducidos. La evidencia visual apoya la comparación del microclima.',
          },
          {
            url: stationSt001,
            alt: 'Retro modulo - Temperatura',
            altEn: 'Rear module - Temperature',

            altEs: 'Módulo trasero - Temperatura',
            caption: 'Immagine nel retro modulo che documenta le condizioni durante le misure di temperatura. L\'ombreggiamento dei pannelli appare coerente con la riduzione dei picchi termici. L\'evidenza visiva supporta il confronto microclimatico.',
            captionEn: 'Rear module image documenting conditions during temperature measurements. Panel shading aligns with reduced thermal peaks. The visual evidence supports the microclimate comparison.',

            captionEs: 'Imagen del módulo trasero que documenta las condiciones durante las mediciones de temperatura. El sombreado del panel se alinea con picos térmicos reducidos. La evidencia visual apoya la comparación del microclima.',
          },
        ],
      },
      explanation: 'Il retro modulo mantiene temperature piu basse durante le ore di picco, riducendo lo stress termico.',
      explanationEn: 'The rear module area keeps lower temperatures during peak hours, reducing heat stress.',

      explanationEs: 'El área del módulo trasero mantiene temperaturas más bajas durante las horas pico, lo que reduce el estrés por calor.',
      approved: false,
    },
    {
      id: 'humidity',
      name: 'Umidita relativa',
      nameEn: 'Relative humidity',

      nameEs: 'Humedad relativa',
      description: 'Differenze percentuali di umidita tra le due aree.',
      descriptionEn: 'Percent humidity differences between the two areas.',

      descriptionEs: 'Diferencias porcentuales de humedad entre las dos áreas.',
      charts: [
        {
          id: 'humidity-gap',
          title: 'Umidita piu alta sotto i moduli',
          titleEn: 'Higher humidity under modules',

          titleEs: 'Mayor humedad debajo de los módulos.',
          note: 'L\'umidita relativa sotto i moduli si mantiene piu alta rispetto al campo aperto lungo il periodo osservato. L\'ombreggiamento limita l\'evaporazione e favorisce un microclima piu umido. Il grafico aiuta a collegare questi valori al minor stress idrico.',
          noteEn: 'Relative humidity under the modules stays higher than in the open field throughout the observed period. Shading limits evaporation and promotes a more humid microclimate. The chart helps link these values to reduced water stress.',

          noteEs: 'La humedad relativa debajo de los módulos se mantiene más alta que en campo abierto durante todo el período observado. La sombra limita la evaporación y promueve un microclima más húmedo. El gráfico ayuda a vincular estos valores con la reducción del estrés hídrico.',
          unit: '%',
          data: [
            { label: 'May', value: 4.2 },
            { label: 'Jun', value: 5.0 },
            { label: 'Jul', value: 6.1 },
            { label: 'Aug', value: 5.4 },
            { label: 'Sep', value: 4.1 },
          ],
        },
        {
          id: 'humidity-event',
          title: 'Giornata secca',
          titleEn: 'Dry day',

          titleEs: 'dia seco',
          note: 'Nelle giornate piu secche la differenza di umidita si riduce, pur restando percepibile. Questo indica che l\'effetto dei moduli e presente anche in condizioni meno favorevoli. La serie temporale fornisce un riferimento per confronti futuri.',
          noteEn: 'On drier days, the humidity difference narrows, though still noticeable. This indicates the module effect persists even in less favorable conditions. The time series provides a reference for future comparisons.',

          noteEs: 'En los días más secos, la diferencia de humedad se reduce, aunque sigue siendo notable. Esto indica que el efecto del módulo persiste incluso en condiciones menos favorables. La serie temporal proporciona una referencia para futuras comparaciones.',
          unit: '%',
          data: [
            { label: 'Week 1', value: 2.1 },
            { label: 'Week 2', value: 2.6 },
            { label: 'Week 3', value: 3.8 },
            { label: 'Week 4', value: 3.1 },
            { label: 'Week 5', value: 2.4 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt002,
            alt: 'Campo aperto - Umidita',
            altEn: 'Open field - Humidity',

            altEs: 'Campo abierto - Humedad',
            caption: 'Scatto in campo aperto associato alle misure di umidita relativa. La scena mostra l\'esposizione piena e aiuta a spiegare i valori inferiori rispetto al retro modulo. L\'immagine completa l\'evidenza dei dati climatici.',
            captionEn: 'Open field shot associated with relative humidity measurements. The scene shows full exposure and helps explain lower values compared to the rear module. The image completes the climatic data evidence.',

            captionEs: 'Toma de campo abierto asociada a mediciones de humedad relativa. La escena muestra una exposición completa y ayuda a explicar los valores más bajos en comparación con el módulo trasero. La imagen completa la evidencia de datos climáticos.',
          },
          {
            url: stationSt003,
            alt: 'Campo aperto - Umidita',
            altEn: 'Open field - Humidity',

            altEs: 'Campo abierto - Humedad',
            caption: 'Scatto in campo aperto associato alle misure di umidita relativa. La scena mostra l\'esposizione piena e aiuta a spiegare i valori inferiori rispetto al retro modulo. L\'immagine completa l\'evidenza dei dati climatici.',
            captionEn: 'Open field shot associated with relative humidity measurements. The scene shows full exposure and helps explain lower values compared to the rear module. The image completes the climatic data evidence.',

            captionEs: 'Toma de campo abierto asociada a mediciones de humedad relativa. La escena muestra una exposición completa y ayuda a explicar los valores más bajos en comparación con el módulo trasero. La imagen completa la evidencia de datos climáticos.',
          },
        ],
        retroModulo: [
          {
            url: stationSt001,
            alt: 'Retro modulo - Umidita',
            altEn: 'Rear module - Humidity',

            altEs: 'Módulo trasero - Humedad',
            caption: 'Scatto nel retro modulo per il monitoraggio dell\'umidita relativa. L\'ombreggiamento e la copertura vegetale osservati favoriscono un microclima piu umido. Questa foto integra i risultati dei grafici.',
            captionEn: 'Rear module shot for relative humidity monitoring. Observed shading and vegetation cover promote a more humid microclimate. This photo complements the chart results.',

            captionEs: 'Toma del módulo trasero para monitoreo de humedad relativa. La sombra y la cubierta vegetal observadas promueven un microclima más húmedo. Esta foto complementa los resultados del gráfico.',
          },
          {
            url: stationSt002,
            alt: 'Retro modulo - Umidita',
            altEn: 'Rear module - Humidity',

            altEs: 'Módulo trasero - Humedad',
            caption: 'Scatto nel retro modulo per il monitoraggio dell\'umidita relativa. L\'ombreggiamento e la copertura vegetale osservati favoriscono un microclima piu umido. Questa foto integra i risultati dei grafici.',
            captionEn: 'Rear module shot for relative humidity monitoring. Observed shading and vegetation cover promote a more humid microclimate. This photo complements the chart results.',

            captionEs: 'Toma del módulo trasero para monitoreo de humedad relativa. La sombra y la cubierta vegetal observadas promueven un microclima más húmedo. Esta foto complementa los resultados del gráfico.',
          },
        ],
      },
      explanation: 'L\'umidita resta piu alta sotto i moduli, utile per ridurre lo stress idrico nelle giornate asciutte.',
      explanationEn: 'Humidity remains higher under the modules, helping reduce water stress on dry days.',

      explanationEs: 'La humedad sigue siendo mayor debajo de los módulos, lo que ayuda a reducir el estrés hídrico en los días secos.',
      approved: false,
    },
    {
      id: 'irradiation',
      name: 'Irradiazione',
      nameEn: 'Irradiation',

      nameEs: 'Irradiación',
      description: 'Riduzione della radiazione solare nelle aree ombreggiate.',
      descriptionEn: 'Reduction of solar radiation in shaded areas.',

      descriptionEs: 'Reducción de la radiación solar en zonas de sombra.',
      charts: [
        {
          id: 'irradiation-gap',
          title: 'Schermatura regolare',
          titleEn: 'Consistent shading',

          titleEs: 'Sombreado consistente',
          note: 'La radiazione sotto i moduli e costantemente inferiore rispetto al campo aperto. La riduzione e stabile e contribuisce a mitigare l\'eccesso di luce nei periodi di picco. Il grafico indica che i livelli restano comunque sufficienti per la coltura.',
          noteEn: 'Radiation under the modules is consistently lower than in the open field. The reduction is stable and helps mitigate excess light during peak periods. The chart indicates levels remain sufficient for the crop.',

          noteEs: 'La radiación debajo de los módulos es consistentemente menor que en campo abierto. La reducción es estable y ayuda a mitigar el exceso de luz durante las horas punta. El gráfico indica que los niveles siguen siendo suficientes para el cultivo.',
          unit: 'W/m2',
          data: [
            { label: 'May', value: 180 },
            { label: 'Jun', value: 220 },
            { label: 'Jul', value: 260 },
            { label: 'Aug', value: 240 },
            { label: 'Sep', value: 200 },
          ],
        },
        {
          id: 'irradiation-event',
          title: 'Picco radiazione',
          titleEn: 'Radiation peak',

          titleEs: 'Pico de radiación',
          note: 'Nel periodo di massima irradiazione si osserva la differenza piu elevata tra le due aree. Il dato evidenzia la capacita dei moduli di schermare gli eccessi senza azzerare la luce disponibile. La curva supporta l\'analisi dei benefici microclimatici.',
          noteEn: 'During peak irradiation, the largest difference between the two areas is observed. The data highlights the ability of modules to screen excess without eliminating available light. The curve supports analysis of microclimate benefits.',

          noteEs: 'Durante el pico de irradiación, se observa la mayor diferencia entre las dos áreas. Los datos resaltan la capacidad de los módulos para proteger el exceso sin eliminar la luz disponible. La curva respalda el análisis de los beneficios del microclima.',
          unit: 'W/m2',
          data: [
            { label: 'Week 1', value: 160 },
            { label: 'Week 2', value: 190 },
            { label: 'Week 3', value: 310 },
            { label: 'Week 4', value: 240 },
            { label: 'Week 5', value: 210 },
          ],
        },
      ],
      images: {
        campoAperto: [
          {
            url: stationSt003,
            alt: 'Campo aperto - Irradiazione',
            altEn: 'Open field - Irradiation',

            altEs: 'Campo abierto - Irradiación',
            caption: 'Immagine di campo aperto collegata alle misure di irradiazione. L\'assenza di schermature rende evidente la maggiore intensita luminosa. La foto aiuta a comprendere la differenza rispetto al retro modulo.',
            captionEn: 'Open field image linked to irradiation measurements. The absence of shading makes the higher light intensity evident. The photo helps explain the difference versus the rear module.',

            captionEs: 'Imagen de campo abierto vinculada a mediciones de irradiación. La ausencia de sombras hace evidente la mayor intensidad de la luz. La foto ayuda a explicar la diferencia con el módulo trasero.',
          },
          {
            url: stationSt001,
            alt: 'Campo aperto - Irradiazione',
            altEn: 'Open field - Irradiation',

            altEs: 'Campo abierto - Irradiación',
            caption: 'Immagine di campo aperto collegata alle misure di irradiazione. L\'assenza di schermature rende evidente la maggiore intensita luminosa. La foto aiuta a comprendere la differenza rispetto al retro modulo.',
            captionEn: 'Open field image linked to irradiation measurements. The absence of shading makes the higher light intensity evident. The photo helps explain the difference versus the rear module.',

            captionEs: 'Imagen de campo abierto vinculada a mediciones de irradiación. La ausencia de sombras hace evidente la mayor intensidad de la luz. La foto ayuda a explicar la diferencia con el módulo trasero.',
          },
        ],
        retroModulo: [
          {
            url: stationSt002,
            alt: 'Retro modulo - Irradiazione',
            altEn: 'Rear module - Irradiation',

            altEs: 'Módulo trasero - Irradiación',
            caption: 'Immagine nel retro modulo durante le misure di irradiazione. La schermatura dei pannelli visibile in foto supporta la riduzione costante registrata dai sensori. Questa evidenza visiva completa il confronto tra le due aree.',
            captionEn: 'Rear module image during irradiation measurements. Panel shading visible in the photo supports the consistent reduction recorded by sensors. This visual evidence completes the comparison between the two areas.',

            captionEs: 'Imagen del módulo trasero durante las mediciones de irradiación. El sombreado del panel visible en la foto respalda la reducción constante registrada por los sensores. Esta evidencia visual completa la comparación entre las dos áreas.',
          },
          {
            url: stationSt003,
            alt: 'Retro modulo - Irradiazione',
            altEn: 'Rear module - Irradiation',

            altEs: 'Módulo trasero - Irradiación',
            caption: 'Immagine nel retro modulo durante le misure di irradiazione. La schermatura dei pannelli visibile in foto supporta la riduzione costante registrata dai sensori. Questa evidenza visiva completa il confronto tra le due aree.',
            captionEn: 'Rear module image during irradiation measurements. Panel shading visible in the photo supports the consistent reduction recorded by sensors. This visual evidence completes the comparison between the two areas.',

            captionEs: 'Imagen del módulo trasero durante las mediciones de irradiación. El sombreado del panel visible en la foto respalda la reducción constante registrada por los sensores. Esta evidencia visual completa la comparación entre las dos áreas.',
          },
        ],
      },
      explanation: 'La schermatura riduce la radiazione diretta, mantenendo comunque livelli sufficienti per la coltura.',
      explanationEn: 'Shading reduces direct radiation while maintaining levels sufficient for the crop.',

      explanationEs: 'El sombreado reduce la radiación directa manteniendo niveles suficientes para el cultivo.',
      approved: false,
    },
  ],
};

export const M4_AI_INSIGHT = {
  summary: 'Le differenze microclimatiche indicano benefici per la coltura nelle ore di massimo stress, senza penalizzare la radiazione necessaria.',
  summaryEn: 'Microclimate differences indicate benefits for the crop during peak stress hours, without reducing the radiation needed.',

  summaryEs: 'Las diferencias microclimáticas indican beneficios para el cultivo durante las horas de mayor estrés, sin reducir la radiación necesaria.',
  highlights: [
    'Temperatura mediamente piu bassa sotto i moduli',
    'Umidita relativa piu alta nelle settimane secche',
    'Irradiazione ridotta ma ancora ottimale',
  ],
  highlightsEn: [
    'Average temperature lower under the modules',
    'Relative humidity higher during dry weeks',
    'Irradiation reduced but still optimal',
  ],
};

export const M4_REVIEW_DATA = {
  summary: '',
};

// ============ MODULE 5: SOIL FERTILITY & RECOVERY ============

export const M5_EVIDENCE_DATA = {
  soilHealth: {
    ph: { value: 6.8, optimal: '6.5-7.0', status: 'optimal' },
    organicMatter: { value: 3.2, unit: '%', optimal: '2.5-4.0%', status: 'optimal' },
    nitrogen: { value: 0.15, unit: '%', optimal: '0.10-0.20%', status: 'optimal' },
    phosphorus: { value: 25, unit: 'ppm', optimal: '20-30 ppm', status: 'optimal' },
    potassium: { value: 180, unit: 'ppm', optimal: '150-200 ppm', status: 'optimal' },
  },
  recoveryTrends: [
    { date: '2022-01', organicMatter: 2.8, nitrogen: 0.12 },
    { date: '2022-07', organicMatter: 2.9, nitrogen: 0.13 },
    { date: '2023-01', organicMatter: 3.0, nitrogen: 0.14 },
    { date: '2023-07', organicMatter: 3.1, nitrogen: 0.14 },
    { date: '2024-01', organicMatter: 3.2, nitrogen: 0.15 },
    { date: '2024-07', organicMatter: 3.2, nitrogen: 0.15 },
  ],
  soilTests: [
    { date: new Date('2024-01-15'), ph: 6.7, organicMatter: 3.1, nitrogen: 0.14 },
    { date: new Date('2024-07-20'), ph: 6.8, organicMatter: 3.2, nitrogen: 0.15 },
  ],
};

export const M5_AI_INSIGHT = {
  summary: 'Soil fertility analysis indicates optimal conditions across all measured parameters. Organic matter content has shown steady improvement over the past 2 years, indicating successful soil recovery practices.',
  recovery: {
    status: 'improving',
    trend: 'Steady increase in organic matter and nitrogen levels',
    timeframe: '2 years',
  },
  recommendations: [
    'Continue current soil management practices',
    'Maintain organic matter levels through cover crops',
    'Monitor nitrogen levels for optimal crop nutrition',
  ],
};

export const M5_REVIEW_DATA = {
  summary: '',
  claims: [
    {
      id: 'm5-claim-1',
      title: 'Soil fertility parameters are optimal',
      text: 'All measured soil parameters (pH, organic matter, nitrogen, phosphorus, potassium) are within optimal ranges.',
      citations: [{ label: 'Soil Test Report 2024', url: '#' }],
      provenance: ['station'],
    },
  ],
};

// ============ MODULE 6: EXECUTIVE SUMMARY ============

export const M6_GENERATED_CONTENT = {
  overview: 'This report provides a comprehensive overview of agricultural compliance for 2024. Field conditions are optimal with all agronomical indicators within normal ranges. The wheat crop is progressing normally through the tillering stage with no immediate concerns detected.',
  production: 'Production analysis indicates significant deviation from RICA benchmarks. Current production levels are approximately 9.6% of the benchmark, which is well below the 30% threshold. This suggests potential issues with crop management, soil conditions, or environmental factors that require immediate attention.',
  water: 'Water consumption of 14,070 m³ is within regulatory limits and shows 4% improvement over previous year. Peak consumption occurs during summer months (June-August), which aligns with crop water requirements. Water sources are primarily groundwater (64%), with surface water (28%) and rainwater (8%) contributing to the mix.',
  microclimate: 'Agrivoltaic system maintains optimal microclimate conditions with temperature reduction of 2-3°C under solar panels compared to open field. Humidity is 3-5% higher under solar panels, and light intensity is reduced by 20-25% while still remaining within optimal range for crop growth. The system positively impacts crop microclimate by reducing temperature stress.',
  soil: 'Soil fertility parameters are optimal across all measured indicators. Organic matter content has shown steady improvement over the past 2 years, indicating successful soil recovery practices. pH levels (6.8), nitrogen (0.15%), phosphorus (25 ppm), and potassium (180 ppm) are all within optimal ranges.',
};

export const M6_AI_INSIGHT = {
  summary: 'Overall assessment indicates mixed results across measured parameters. While field conditions, water management, microclimate, and soil health are optimal, production levels are significantly below RICA benchmarks. The agrivoltaic system demonstrates positive impact on crop microclimate while maintaining adequate growing conditions. Immediate attention is required to address production shortfalls, which may be related to crop management practices, nutrient availability, or environmental stress factors.',
  keyRecommendations: [
    'Conduct detailed root cause analysis to identify factors limiting production below RICA benchmarks',
    'Continue current water management practices while monitoring groundwater sustainability',
    'Expand agrivoltaic system based on positive microclimate results showing temperature and humidity benefits',
    'Maintain soil fertility through continued organic matter management and nutrient monitoring',
    'Review crop management practices including planting density, fertilization schedules, and pest management',
    'Implement enhanced monitoring and early intervention protocols for production optimization',
  ],
};

export const M6_REVIEW_DATA = {
  summary: '',
  claims: [
    {
      id: 'm6-claim-1',
      title: 'Report meets all RICA compliance requirements',
      text: 'All modules have been reviewed and validated, confirming compliance with RICA standards for 2024.',
      citations: [{ label: 'RICA Compliance Checklist', url: '#' }],
      provenance: ['manual'],
      validated: false,
    },
    {
      id: 'm6-claim-2',
      title: 'Agrivoltaic system demonstrates positive environmental impact',
      text: 'Microclimate analysis confirms the agrivoltaic system maintains optimal growing conditions while reducing temperature stress.',
      citations: [{ label: 'Microclimate Monitoring Report', url: '#' }, { label: 'Module 4 Analysis', url: '#' }],
      provenance: ['station'],
      validated: true,
    },
  ],
};

// Helper function to get module data
export function getModuleData(moduleId, locale) {
  const resolvedLocale = resolveLocale(locale);
  let data = null;
  switch (moduleId) {
    case 'm0':
      data = {
        evidence: M0_EVIDENCE_DATA,
        aiInsight: M0_AI_INSIGHT,
        review: M0_REVIEW_DATA,
      };
      break;
    case 'm1':
      data = {
        evidence: M1_EVIDENCE_DATA,
        aiInsight: M1_AI_INSIGHT,
        review: M1_REVIEW_DATA,
      };
      break;
    case 'm2':
      data = {
        evidence: M2_EVIDENCE_DATA,
        aiInsight: M2_AI_INSIGHT,
        review: M2_REVIEW_DATA,
      };
      break;
    case 'm3':
      data = {
        evidence: M3_EVIDENCE_DATA,
        aiInsight: M3_AI_INSIGHT,
        review: M3_REVIEW_DATA,
      };
      break;
    case 'm4':
      data = {
        evidence: M4_EVIDENCE_DATA,
        aiInsight: M4_AI_INSIGHT,
        review: M4_REVIEW_DATA,
      };
      break;
    case 'm5':
      data = {
        evidence: M5_EVIDENCE_DATA,
        aiInsight: M5_AI_INSIGHT,
        review: M5_REVIEW_DATA,
      };
      break;
    case 'm6':
      data = {
        evidence: M6_GENERATED_CONTENT,
        aiInsight: M6_AI_INSIGHT,
        review: M6_REVIEW_DATA,
      };
      break;
    default:
      data = null;
  }
  return data ? localizeObject(data, resolvedLocale) : null;
}
